import React, { useMemo, type ReactNode } from "react";
import { Icon, Placeholder } from "semantic-ui-react";
import cn from "classnames";
import CroppedThumbnail from "../../croppedThumbnail/CroppedThumbnail";
import type { RemovableCardProps } from "../removableCard/RemovableCard";
import { HiddenContent } from "components/HiddenContent/HiddenContent";

import "./packContentListItem.scss";

export type PackContentListItemProps = RemovableCardProps & { publisher?: string };

const PackContentListItem: React.FC<PackContentListItemProps> = (props) => {
  const handleRemoveIconClick = () => {
    props.onRemoveItemClick?.(props.id, props.type);
  };

  const thumbnail: ReactNode = useMemo(() => {
    if (!props.thumbnailUrl) {
      return (
        <Placeholder data-testid="loading-pack-thumbnail">
          <Placeholder.Image />
        </Placeholder>
      );
    }
    if (typeof props.thumbnailUrl === "string") {
      return (
        <CroppedThumbnail
          thumbnailUrl={props.thumbnailUrl}
          altText={props.title ? `${props.title} cover image` : "pack content thumbnail"}
          isHidden={props.hidden}
        />
      );
    }
    return (
      <>
        <div className={cn("pack-content-icon-background")}>{props.thumbnailUrl}</div>
        <HiddenContent variant="card" isHidden={!!props.hidden} />
      </>
    );
  }, [props.title, props.thumbnailUrl, props.hidden]);

  return (
    <div key={props.id} className="pack-content-list-item" data-testid="pack-content-list-item">
      <div className="thumb">{thumbnail}</div>
      <div className="body">
        {props.publisher && (
          <div className="vendor" data-testid="pack-content-vendor">
            {props.publisher}
          </div>
        )}
        <div className="title">
          {props.title || props.title === "" ? (
            <span data-testid="pack-content-title">{props.title}</span>
          ) : (
            <Placeholder data-testid="loading-pack-data">
              <Placeholder.Header>
                <Placeholder.Line />
                <Placeholder.Line />
              </Placeholder.Header>
            </Placeholder>
          )}
        </div>
        {props.meta && (
          <div className="meta-text" data-testid="pack-content-meta">
            {props.meta}
          </div>
        )}
      </div>
      {props.onRemoveItemClick && !props.isReadOnly && (
        <div className="close-icon">
          <Icon
            fitted
            name="close"
            link
            onClick={handleRemoveIconClick}
            aria-label="remove-item"
            data-testid="remove-item"
          />
        </div>
      )}
    </div>
  );
};

export default PackContentListItem;
